import { useEffect, useState } from "react";
import { Save, LayoutTemplate, Newspaper, Plus, Trash2, Pencil, X, Eye, EyeOff } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PublicAPI, AdminAPI } from "@/lib/api";
import { formatDate } from "@/lib/dummyData";
import { toast } from "sonner";

// Halaman CMS "Setting Homepage" (khusus Super Admin):
//  - Tab "Konten Homepage": edit teks & statistik landing page.
//  - Tab "Berita / News": CRUD pengumuman yang tampil di /berita.
export default function HomepageSettings() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Setting Homepage</h1>
        <p className="text-ink/75 mt-2 text-sm">Ubah konten landing page dan kelola berita — perubahan langsung tampil ke publik.</p>
      </div>

      <Tabs defaultValue="konten">
        <TabsList className="bg-cream rounded-full p-1">
          <TabsTrigger value="konten" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white px-5" data-testid="hp-tab-konten"><LayoutTemplate size={14} /> Konten Homepage</TabsTrigger>
          <TabsTrigger value="news" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white px-5" data-testid="hp-tab-news"><Newspaper size={14} /> Berita / News</TabsTrigger>
        </TabsList>

        <TabsContent value="konten" className="pt-6"><HomepageContent /></TabsContent>
        <TabsContent value="news" className="pt-6"><NewsManager /></TabsContent>
      </Tabs>
    </div>
  );
}

// ===== Tab 1: Konten Homepage =====
function HomepageContent() {
  const [hp, setHp] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { PublicAPI.homepage().then(setHp).catch(() => toast.error("Gagal memuat konten")); }, []);

  const set = (k) => (e) => setHp((h) => ({ ...h, [k]: e.target.value }));
  const setStat = (i, k) => (e) => setHp((h) => {
    const stats = [...(h.stats || [])];
    stats[i] = { ...stats[i], [k]: e.target.value };
    return { ...h, stats };
  });

  const save = async () => {
    setSaving(true);
    try { await AdminAPI.updateHomepage(hp); toast.success("Konten homepage disimpan"); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  if (!hp) return <div className="text-ink/55 py-10 text-center">Memuat…</div>;

  return (
    <div className="space-y-5 max-w-3xl">
      <Card className="rounded-2xl border-stone-soft p-6 space-y-4">
        <h3 className="font-display font-semibold text-ink">Bagian Hero</h3>
        <Field label="Badge / Pengumuman kecil"><Input value={hp.hero_badge || ""} onChange={set("hero_badge")} data-testid="hp-hero-badge" /></Field>
        <Field label="Judul Utama"><Input value={hp.hero_title || ""} onChange={set("hero_title")} data-testid="hp-hero-title" /></Field>
        <Field label="Kata yang di-italic (highlight)"><Input value={hp.hero_highlight || ""} onChange={set("hero_highlight")} /></Field>
        <Field label="Subjudul"><Textarea rows={3} value={hp.hero_subtitle || ""} onChange={set("hero_subtitle")} data-testid="hp-hero-subtitle" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Label Tombol Utama"><Input value={hp.cta_primary || ""} onChange={set("cta_primary")} /></Field>
          <Field label="Label Tombol Kedua"><Input value={hp.cta_secondary || ""} onChange={set("cta_secondary")} /></Field>
        </div>
      </Card>

      <Card className="rounded-2xl border-stone-soft p-6 space-y-4">
        <h3 className="font-display font-semibold text-ink">Statistik (4 angka)</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {(hp.stats || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-stone-soft p-3 space-y-2">
              <Input value={s.label} onChange={setStat(i, "label")} placeholder="Label" className="text-sm" />
              <div className="flex gap-2">
                <Input value={s.value} onChange={setStat(i, "value")} placeholder="Nilai" className="text-sm" />
                <Input value={s.suffix} onChange={setStat(i, "suffix")} placeholder="Suffix" className="w-20 text-sm" />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="rounded-2xl border-stone-soft p-6 space-y-4">
        <h3 className="font-display font-semibold text-ink">Kartu Promo & Heading Fitur</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Promo Label"><Input value={hp.promo_label || ""} onChange={set("promo_label")} /></Field>
          <Field label="Promo Nilai"><Input value={hp.promo_value || ""} onChange={set("promo_value")} /></Field>
          <Field label="Promo Catatan"><Input value={hp.promo_note || ""} onChange={set("promo_note")} /></Field>
        </div>
        <Field label="Heading Bagian Fitur"><Textarea rows={2} value={hp.features_heading || ""} onChange={set("features_heading")} /></Field>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-full bg-forest hover:bg-forest-dark text-white shadow-lg" data-testid="hp-save">
          <Save size={15} /> {saving ? "Menyimpan…" : "Simpan Konten"}
        </Button>
      </div>
    </div>
  );
}

// ===== Tab 2: Berita / News CRUD =====
const emptyNews = { title: "", category: "Pengumuman", excerpt: "", image_url: "", is_published: true };

function NewsManager() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // objek berita yang diedit / dibuat
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setList(await AdminAPI.news()); } catch { toast.error("Gagal memuat berita"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title) return toast.error("Judul wajib diisi");
    try {
      if (editing.id) await AdminAPI.updateNews(editing.id, editing);
      else await AdminAPI.createNews(editing);
      toast.success("Berita disimpan");
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.error || "Gagal menyimpan"); }
  };

  const remove = async (id) => {
    try { await AdminAPI.deleteNews(id); toast.success("Berita dihapus"); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal menghapus"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-display font-semibold text-ink text-lg">Kelola Berita</h3>
        <Button onClick={() => setEditing({ ...emptyNews })} className="rounded-lg bg-forest hover:bg-forest-dark text-white" data-testid="news-add">
          <Plus size={15} /> Berita Baru
        </Button>
      </div>

      {/* Form editor (muncul saat menambah/mengedit) */}
      {editing && (
        <Card className="rounded-2xl border-2 border-forest/30 p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-display font-semibold text-ink">{editing.id ? "Edit Berita" : "Berita Baru"}</h4>
            <button onClick={() => setEditing(null)} className="text-ink/55 hover:text-ink"><X size={18} /></button>
          </div>
          <Field label="Judul"><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="news-title" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Kategori"><Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></Field>
            <Field label="URL Gambar"><Input value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} /></Field>
          </div>
          <Field label="Ringkasan / Isi"><Textarea rows={3} value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm text-ink/75 cursor-pointer">
            <input type="checkbox" checked={!!editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} className="accent-forest" />
            Terbitkan (tampil di publik)
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={save} data-testid="news-save"><Save size={15} /> Simpan</Button>
          </div>
        </Card>
      )}

      <Card className="rounded-2xl border-stone-soft p-5">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
              <TableHead className="text-[10px] uppercase tracking-wider">Judul</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Kategori</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Tanggal</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-ink/55 py-10">Memuat…</TableCell></TableRow>
            ) : list.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-ink/55 py-10">Belum ada berita.</TableCell></TableRow>
            ) : list.map((n) => (
              <TableRow key={n.id} className="border-stone-soft hover:bg-cream/30" data-testid={`news-row-${n.id}`}>
                <TableCell className="font-medium text-sm text-ink max-w-[320px] truncate">{n.title}</TableCell>
                <TableCell><Badge className="bg-stone-soft/40 text-ink/70 border-0 rounded-full text-[10px]">{n.category}</Badge></TableCell>
                <TableCell className="text-sm text-ink/70">{formatDate(n.published_at)}</TableCell>
                <TableCell>
                  {n.is_published
                    ? <span className="inline-flex items-center gap-1 text-forest text-xs"><Eye size={12} /> Terbit</span>
                    : <span className="inline-flex items-center gap-1 text-ink/45 text-xs"><EyeOff size={12} /> Draft</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" className="rounded-lg h-8 w-8" onClick={() => setEditing(n)} data-testid={`news-edit-${n.id}`}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" className="rounded-lg h-8 w-8 text-terracotta hover:bg-terracotta/10" onClick={() => remove(n.id)} data-testid={`news-delete-${n.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-ink/55 uppercase tracking-wider">{label}</Label>
    {children}
  </div>
);
